import { Project } from 'src/app/core/objects/project';
import {
  DeletePalletLayerInterlayer,
  GroupAddLayer,
  GroupAddLayerInterlayer,
  GroupLayerAdd,
  InterlayerConfig,
  Load,
  PalletInterlayer,
  PalletLayer,
  PalletLayerGroup,
  PalletLayerGroupEdit,
} from 'src/app/core/types';
import {
  getActivePalletLayerGroups,
  getArrangementLayersFromGroups,
} from 'src/app/shared/helpers/pallet-loading-space.helper';

/** One arrangement type row in the pallet layer group edit dialog. */
export interface EditablePalletLayerRow {
  arrangementLayer: PalletLayer;
  committedLayerId: number | null;
  layersLoadedQuantity: number;
  /** False when calculate returned a type not present in the committed group. */
  saveable: boolean;
}

export function palletInterlayerToInterlayerConfig(interlayer: PalletInterlayer): InterlayerConfig {
  const raw = interlayer as PalletInterlayer & { thickness?: number };
  return {
    name: interlayer.name,
    thickness: interlayer.dimensions?.height ?? raw.thickness ?? 0,
    everyNLayers: interlayer.regularity,
    placeLastOnTopLayer: interlayer.onTop,
    placeLastOnFirstLayer: interlayer.onBottom,
  };
}

export function mapInterlayerConfigToGroupAddInterlayer(
  cfg: InterlayerConfig,
  color: number,
  weight: number,
): GroupAddLayerInterlayer {
  return {
    name: cfg.name,
    color,
    thickness: cfg.thickness,
    weight: weight ?? 0,
    onTop: !!cfg.placeLastOnTopLayer,
    onBottom: !!cfg.placeLastOnFirstLayer,
    regularity: cfg.everyNLayers,
  };
}

export function interlayerConfigToPalletInterlayer(
  cfg: InterlayerConfig,
  color: number,
  weight: number,
  footprint?: { width: number; length: number },
): PalletInterlayer {
  return {
    name: cfg.name,
    color,
    weight: weight ?? 0,
    onTop: !!cfg.placeLastOnTopLayer,
    onBottom: !!cfg.placeLastOnFirstLayer,
    regularity: cfg.everyNLayers ?? 0,
    dimensions: {
      width: footprint?.width ?? 0,
      length: footprint?.length ?? 0,
      height: cfg.thickness ?? 0,
    },
    loadedInterlayers: [],
  };
}

export function buildGroupAddLayerPayload(params: {
  layerId: number;
  layerQuantity: number;
  interlayer?: InterlayerConfig;
  interlayerColor?: number;
  interlayerWeight?: number;
}): GroupAddLayer {
  const layer: GroupAddLayer = {
    layerId: params.layerId,
    quantity: params.layerQuantity,
  };
  if (params.interlayer) {
    layer.interlayer = mapInterlayerConfigToGroupAddInterlayer(
      params.interlayer,
      params.interlayerColor ?? 0,
      params.interlayerWeight ?? 0,
    );
  }
  return layer;
}

export function resolveInterlayerTemplateMeta(
  templateLoad: Load | undefined,
  fallbackColor: number,
): { color: number; weight: number } {
  return {
    color: templateLoad?.color ?? fallbackColor,
    weight: templateLoad?.weight ?? 0,
  };
}

export function buildGroupLayerAddRequest(params: {
  currentWorkSpaceId?: number;
  workSpaceId: number;
  stopId?: number;
  layerId: number;
  layerQuantity: number;
  interlayer?: InterlayerConfig;
  interlayerColor?: number;
  interlayerWeight?: number;
}): GroupLayerAdd {
  const target: GroupLayerAdd['target'] = { workSpaceId: params.workSpaceId };
  if (params.stopId !== undefined) {
    target.stopId = params.stopId;
  }
  return {
    currentWorkSpaceId: params.currentWorkSpaceId,
    target,
    layer: buildGroupAddLayerPayload({
      layerId: params.layerId,
      layerQuantity: params.layerQuantity,
      interlayer: params.interlayer,
      interlayerColor: params.interlayerColor,
      interlayerWeight: params.interlayerWeight,
    }),
  };
}

export function buildPalletLayerGroupEditRequest(params: {
  currentWorkSpaceId?: number;
  workSpaceId: number;
  stopId?: number;
  layerGroupId: number;
  layers?: { layerId: number; quantityDifference: number }[];
  interlayer?: InterlayerConfig;
  interlayerColor?: number;
  interlayerWeight?: number;
}): PalletLayerGroupEdit {
  const body: PalletLayerGroupEdit = {
    currentWorkSpaceId: params.currentWorkSpaceId,
    target: { workSpaceId: params.workSpaceId },
    layerGroupId: params.layerGroupId,
  };
  if (params.stopId !== undefined) {
    body.target.stopId = params.stopId;
  }
  if (params.layers?.length) {
    body.layers = params.layers;
  }
  if (params.interlayer) {
    body.interlayer = mapInterlayerConfigToGroupAddInterlayer(
      params.interlayer,
      params.interlayerColor ?? 0,
      params.interlayerWeight ?? 0,
    );
  }
  return body;
}

export function buildPalletLayerInterlayerDeleteRequest(params: {
  currentWorkSpaceId?: number;
  workSpaceId: number;
  stopId?: number;
  layerGroupId: number;
}): DeletePalletLayerInterlayer {
  const body: DeletePalletLayerInterlayer = {
    currentWorkSpaceId: params.currentWorkSpaceId,
    target: { workSpaceId: params.workSpaceId },
    layerGroupId: params.layerGroupId,
  };
  if (params.stopId !== undefined) {
    body.target.stopId = params.stopId;
  }
  return body;
}

export function buildLoadFromPalletLayerGroup(group: PalletLayerGroup): Load {
  const dims = group.dimensions;
  return {
    color: group.color,
    loadType: group.loadType ?? 0,
    name: group.name ?? '',
    length: dims?.length ?? null,
    width: dims?.width ?? null,
    height: dims?.height ?? null,
    diameter: dims?.diameter ?? null,
    weight: group.weight ?? null,
    quantity: group.quantity ?? 1,
    stacking: group.stackingOptions?.stacking ?? false,
    stackingOptions: group.stackingOptions,
    show: true,
  };
}

export function layerArrangementSignature(layer: PalletLayer): string {
  const height = Math.round(layer.dimensions?.height ?? 0);
  const qty = layer.quantity ?? 0;
  return `${qty}:${height}`;
}

export function matchArrangementLayerToCommitted(
  arrangementLayer: PalletLayer,
  committedLayers: PalletLayer[],
): PalletLayer | undefined {
  const signature = layerArrangementSignature(arrangementLayer);
  return (committedLayers ?? []).find(
    (committed) => layerArrangementSignature(committed) === signature,
  );
}

/** Arrangement edit rows from palletLayersGroups already stored on the project. */
export function buildEditablePalletLayerRowsFromProject(
  project: Project | undefined | null,
  workSpaceIndex: number | undefined,
  stopIndex: number | undefined,
  committedGroup: PalletLayerGroup,
): EditablePalletLayerRow[] {
  const groups = getActivePalletLayerGroups(project, workSpaceIndex, stopIndex);
  const arrangementLayers = getArrangementLayersFromGroups(groups, committedGroup.color, {
    includeZeroQuantity: true,
    mergeAllMatchingGroups: true,
    limit: 3,
  });
  return mergeArrangementWithLoadedCounts(arrangementLayers, committedGroup);
}

export function mergeArrangementWithLoadedCounts(
  arrangementLayers: PalletLayer[],
  committedGroup: PalletLayerGroup,
): EditablePalletLayerRow[] {
  const committedLayers = committedGroup.layers ?? [];
  return (arrangementLayers ?? []).map((arrangementLayer) => {
    const committed = matchArrangementLayerToCommitted(arrangementLayer, committedLayers);
    return {
      arrangementLayer,
      committedLayerId: committed?.id ?? null,
      layersLoadedQuantity: committed?.layersLoadedQuantity ?? 0,
      saveable: committed != null,
    };
  });
}

export function buildLayerQuantityDeltas(
  rows: EditablePalletLayerRow[],
  newCounts: number[],
): { layerId: number; quantityDifference: number }[] {
  const deltas: { layerId: number; quantityDifference: number }[] = [];
  rows.forEach((row, index) => {
    if (!row.saveable || row.committedLayerId == null) {
      return;
    }
    const parsed = newCounts[index];
    const newCount = Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : row.layersLoadedQuantity;
    const delta = newCount - row.layersLoadedQuantity;
    if (delta !== 0) {
      deltas.push({ layerId: row.committedLayerId, quantityDifference: delta });
    }
  });
  return deltas;
}
